import { Box, Button, Stack } from '@mui/material'
import DropDownOA from '../../../common/dropDownOA'
import { Item } from '../../../common/snippets'
import useToken, { originalUserSearchParams } from '../../../helpers/useTokens'
import { GetRequest, PostRequest, PostRequestCachedV3 } from '../../../common/api'
import { API_CACHE_DURATION, urls } from '../../../common/constants'
import { useSetAtom } from 'jotai'
import { alertAtom, globalLoaderAtom } from '../../../common/jotaiState/globalStateAtoms'
import { useEffect, useState } from 'react'
import { openIsolatedTab } from '../../../common/helper'
import { useTranslation } from '../../../../i18n/I18nProvider'

const SelectCompany = () => {
  const { translations } = useTranslation()
  const mtpl = translations?.mtplMaster || {}
  const { loginData } = useToken()
  const { intCompanyID, intBranchID, intUserID } = loginData
  const { IsBranchAdmin, IsMTPLAdmin, IsAdmin } = loginData
  const adminFlag = IsBranchAdmin || IsMTPLAdmin || IsAdmin
  const setLoader = useSetAtom(globalLoaderAtom)
  const setAlert = useSetAtom(alertAtom)
  const [companyList, setCompanyList] = useState([])
  const [selectedCompany, setSelectedCompany] = useState()
  const [branchList, setBranchList] = useState([])
  const [selectedBranch, setSelectedBranch] = useState()
  const [userList, setUserList] = useState([])
  const [selectedUser, setSelectedUser] = useState()

  const getCompanyList = () => {
    setLoader(true)
    const payload = {
      intUserID: loginData?.intUserID
    }
    GetRequest(urls.getCompaniesListAllForMPTLSelectCompany, payload)
      .then((res) => {
        const list = Array.isArray(res?.Data) ? res.Data : res?.Data?.Table
        if (res.status !== 'Success' || !Array.isArray(list)) {
          throw new Error(res?.ErrMessage || mtpl.invalidResponse || 'Invalid Response')
        }
        if (!list.length) {
          throw new Error(mtpl.noDataFound || 'No Data Found')
        }
        const updatedList = [...(list || [])]
          .sort((a, b) =>
            (a.CompanyName || '').localeCompare(b.CompanyName || '')
          )
          .map(company => ({
            ...company,
            CompanyNameWithID: `${company.CompanyName || ''} (${company.CompanyID})`.trim()
          }));
        setCompanyList(updatedList)
        const defaultCompany = updatedList.find((a) => a.CompanyID === intCompanyID) || updatedList[0]
        setSelectedCompany(defaultCompany)
      })
      .catch((err) => {
        setCompanyList([])
        setSelectedCompany()
        setAlert({
          show: true,
          msg: err.message,
          nextId: '',
        })
      })
      .finally(() => {
        setLoader(false)
      })
  }

  const getBranchID = (companyID) => {
    setLoader(true)
    const payload = {
      intUserID: 0,
      intCompanyID: companyID,
      intBranchID: 0,
      strBranchName: '',
      strBranchCode: '',
      intStateID: 0,
      intCityID: 0,
      intIsActive: 1,
    }

    PostRequestCachedV3(urls.getBranchConfigOrEwbillinfo, payload, true, API_CACHE_DURATION)
      .then((res) => {
        if (res.status !== 'Success' || !Array.isArray(res?.Data?.Table)) {
          throw new Error(res?.ErrMessage || mtpl.invalidResponse || 'Invalid Response')
        }
        if (!res.Data.Table.length) {
          throw new Error(mtpl.noDataFound || 'No Data Found')
        }
        const list = res.Data.Table
        setBranchList(list)
        const defaultBranch = list.find((a) => a.BranchID === intBranchID) || list[0]
        setSelectedBranch(defaultBranch)
      })
      .catch((err) => {
        setBranchList([])
        setSelectedBranch()
        setUserList([])
        setSelectedUser()
        setAlert({
          show: true,
          msg: err.message,
          nextId: '',
        })
      })
      .finally(() => {
        setLoader(false)
      })
  }

  const getUserList = (companyID, branchID) => {
    setLoader(true)
    const data = {
      intUserID: 0,
      intCompanyID: companyID,
      intBranchID: branchID,
      strUserName: '',
      strLoginID: '',
      strPassword: '',
      strContactNo: '',
      intRoleID: 0,
      intIsActive: 1,
    }
    PostRequestCachedV3(urls.getUserList, data, true, API_CACHE_DURATION)
      .then((res) => {
        if (res.status !== 'Success' || !Array.isArray(res?.Data?.Table)) {
          throw new Error(res?.ErrMessage || mtpl.invalidResponse || 'Invalid Response')
        }

        if (!res.Data.Table.length) {
          throw new Error(mtpl.noDataFound || 'No Data Found')
        }
        const list = res.Data.Table
        setUserList(list)
        const defaultUser = list.find((a) => a.UserID === intUserID) || list[0]
        setSelectedUser(defaultUser)
      })
      .catch((err) => {
        setUserList([])
        setSelectedUser()
        setAlert({
          show: true,
          msg: err.message,
          nextId: '',
        })
      })
      .finally(() => {
        setLoader(false)
      })
  }

  const handleCompanySelect = (company) => {
    setBranchList([])
    setSelectedBranch()
    setUserList([])
    setSelectedUser()
    setSelectedCompany(company?.CompanyID ? company : undefined)
  }

  useEffect(() => {
    if (intCompanyID) {
      getCompanyList()
    }
  }, [intCompanyID])

  useEffect(() => {
    if (selectedCompany?.CompanyID) {
      getBranchID(selectedCompany.CompanyID)
    }
  }, [selectedCompany?.CompanyID])

  useEffect(() => {
    if (selectedBranch?.BranchID && selectedCompany?.CompanyID) {
      getUserList(selectedCompany.CompanyID, selectedBranch.BranchID)
    }
  }, [selectedBranch?.BranchID])

  const handleLogin = () => {
    if (!adminFlag && selectedUser?.RoleName?.includes('Admin')) {
      setAlert({
        show: true,
        msg: mtpl.nonAdminCannotSelectAdminUsers || 'Non-Admin user can not select Admin Users.',
        nextId: '',
      })
      return
    }
    const data = {
      CompanyID: selectedCompany?.CompanyID,
      UserID: selectedUser?.UserID,
    }
    setLoader(true)
    GetRequest(urls.getAuthenticationToken, data)
      .then((res) => {
        if (res.status !== 'Success' || !res?.ReportURL) {
          throw new Error(res?.ErrMessage || mtpl.invalidResponse || 'Invalid Response')
        }
        const url = `${window.location.origin}/?authentication=${res.AuthenticationToken}&${originalUserSearchParams(loginData)}`
        openIsolatedTab(url)
      })
      .catch((err) => {
        setAlert({
          show: true,
          msg: err.message,
          nextId: '',
        })
      })
      .finally(() => {
        setLoader(false)
      })
  }

  return (
    <div style={{ position: 'absolute', width: '100%' }}>
      <Item sx={{ padding: '0' }} className="reports">
        <Stack sx={{ minHeight: 100, minWidth: 650, marginTop: '8px' }}>
          <Box
            sx={{
              width: '99%',
              marginLeft: '1%',
              display: 'inline-flex',
              padding: '0% 0px',
            }}
          >
            <Item sx={{ minWidth: '20%' }}>
              <DropDownOA
                data={companyList}
                defaultvalue={selectedCompany?.CompanyNameWithID || ''}
                width="100%"
                label={mtpl.selectCompany || 'Select Company'}
                dataKey="CompanyNameWithID"
                idKey="CompanyID"
                onSelect={handleCompanySelect}
              />
            </Item>
            <Item sx={{ minWidth: '20%' }}>
              <DropDownOA
                data={branchList}
                defaultvalue={selectedBranch?.BranchName || ''}
                width="100%"
                label={mtpl.selectBranch || 'Select Branch'}
                dataKey="BranchName"
                idKey="BranchID"
                onSelect={setSelectedBranch}
              />
            </Item>
            <Item sx={{ minWidth: '20%' }}>
              <DropDownOA
                data={userList}
                defaultvalue={selectedUser?.UserName || ''}
                width="100%"
                label={mtpl.selectUser || 'Select User'}
                dataKey="UserName"
                idKey="UserID"
                onSelect={setSelectedUser}
              />
            </Item>
            <div style={{ display: 'flex', gap: '8px', margin: '10px 0px', justifyContent: 'end' }}>
              <Button
                className="select-lr-btn"
                variant="contained"
                color="secondary"
                sx={{ height: '100%', fontSize: '14px' }}
                onClick={handleLogin}
              >
                {mtpl.login || 'Login'}
              </Button>
            </div>
          </Box>
        </Stack>
      </Item>
    </div>
  )
}

export default SelectCompany
